import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Email } from "../../models/EmailModel";
import { deleteEmail, fetchCustomerEmails } from "../../api/emails";
import { Alert, Skeleton, Snackbar } from "@mui/material";
import { AxiosError } from "axios";
import "./CustomerDetails.scss";
import {
  WithData,
  WithAnagraficaClienteID,
} from "../../models/shared/CommonModels";
import { fetchEmailsQueryKey } from "../../utils/QueryClient";
import SimpleDataTable from "../shared/SimpleDataTable";
import DeleteDialog from "../shared/DeleteDialog";
import { GridRenderCellParams } from "@mui/x-data-grid";
import CustomerEmailForm from "./extra/CustomerEmailForm";
import { sendTestEmail } from "../../api/configuration/configuration-email";

interface TestEmailErrorResponse {
  message?: string;
  error_code?: string;
  provider_code?: string | number | null;
}

interface WithEmailRecord extends WithAnagraficaClienteID {
  createFormOpen: boolean;
  toggleCreateFormOpen: () => void;
}

const CustomerEmailsTable: React.FC<WithEmailRecord> = ({
  anagrafica_cliente_id,
  createFormOpen,
  toggleCreateFormOpen,
}) => {
  const queryClient = useQueryClient();

  const [emails, setEmails] = useState<Email[]>(undefined as any);
  const [selectedRowElement, setSelectedRowElement] = useState<Email>();
  const [emailTestLoadingId, setEmailTestLoadingId] = useState<number>();
  const [notification, setNotification] = useState<{
    severity: "success" | "error";
    message: string;
  } | null>(null);

  const [emailDataEditFormOpen, setEmailDataEditFormOpen] =
    useState<boolean>(false);
  const toggleEmailDataEditFormOpen = () =>
    setEmailDataEditFormOpen(!emailDataEditFormOpen);

  const [deleteAlertOpen, setDeleteAlertOpen] = useState<boolean>(false);
  const toggleDeleteAlert = () => setDeleteAlertOpen(!deleteAlertOpen);

  const deleteMutation = useMutation({
    mutationFn: (x: WithData) => deleteEmail(x),
    onSuccess: () => {
      setSelectedRowElement(undefined);
      queryClient.invalidateQueries(fetchEmailsQueryKey);
    },
    onError: () => {},
  });

  const query = useQuery({
    queryKey: [fetchEmailsQueryKey],
    queryFn: () => fetchCustomerEmails(anagrafica_cliente_id),
    onSuccess: (getResponse) => {
      setEmails(getResponse.data);
    },
    refetchOnWindowFocus: false,
  });

  if (query.isFetching || query.isLoading) return <Skeleton animation="wave" />;

  // TODO: verify if (query.isError) return (<Navigate to="/home" replace />)

  const editOperations = (record: GridRenderCellParams<any, Email>) => {
    setSelectedRowElement(record.value);
    toggleEmailDataEditFormOpen();
  };

  const deleteOperations = (record: GridRenderCellParams<any, Email>) => {
    setSelectedRowElement(record.value);
    toggleDeleteAlert();
  };

  const emailTestOperations = async (
    record: GridRenderCellParams<any, Email>
  ) => {
    const email = record.value;
    if (!email) return;
    if (!window.confirm(`Inviare un'email di prova a ${email.email}?`)) return;

    setEmailTestLoadingId(email.id);
    try {
      const request = {} as WithData;
      request.data = { to_address: email.email };
      const response = await sendTestEmail(request);
      setNotification({
        severity: "success",
        message:
          response.data?.message ||
          `Email di prova inviata con successo a ${email.email}`,
      });
    } catch (error) {
      const axiosError = error as AxiosError<TestEmailErrorResponse>;
      const errorData = axiosError.response?.data;
      const errorCode =
        errorData?.error_code || axiosError.response?.status || "UNKNOWN";
      const providerCode = errorData?.provider_code
        ? `, provider: ${errorData.provider_code}`
        : "";
      setNotification({
        severity: "error",
        message: `${errorData?.message || "Invio email di prova non riuscito"} (codice: ${errorCode}${providerCode})`,
      });
    } finally {
      setEmailTestLoadingId(undefined);
    }
  };

  return (
    <>
      <SimpleDataTable<Email>
        columns={[
          {
            field: "email",
            headerName: "Email",
            flex: 1,
          },
          {
            field: "pec",
            headerName: "PEC",
            type: "boolean",
            sortable: false,
            flex: 1,
          },
          {
            field: "preferred",
            headerName: "Preferito",
            type: "boolean",
            sortable: false,
            flex: 1,
          },
        ]}
        rows={
          emails !== undefined
            ? emails.map((d) => {
                return {
                  id: d.id,
                  email: d.email,
                  pec: d.pec,
                  preferred: d.preferred,
                  actions: d,
                  delete_action: d,
                } as unknown as Email;
              })
            : []
        }
        rowCount={emails.length}
        editOperations={editOperations}
        deleteOperations={deleteOperations}
        emailTestOperations={emailTestOperations}
        emailTestLoadingId={emailTestLoadingId}
      />

      {/* Add Email */}
      <CustomerEmailForm
        initialData={{ id_cliente: anagrafica_cliente_id } as Email}
        open={createFormOpen}
        toggleOpen={toggleCreateFormOpen}
      />

      {/* Edit Email */}
      {selectedRowElement && (
        <CustomerEmailForm
          initialData={selectedRowElement}
          open={emailDataEditFormOpen}
          toggleOpen={toggleEmailDataEditFormOpen}
          editEnabled={true}
        />
      )}

      {/* Delete */}
      <DeleteDialog
        open={deleteAlertOpen}
        onClose={toggleDeleteAlert}
        identifier={selectedRowElement?.email}
        onPositive={() => {
          toggleDeleteAlert();
          const x = {} as WithData;
          x.data = selectedRowElement?.id;
          deleteMutation.mutate(x);
        }}
        onNegative={toggleDeleteAlert}
      />
      <Snackbar
        open={notification !== null}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
      >
        <Alert
          severity={notification?.severity || "success"}
          onClose={() => setNotification(null)}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CustomerEmailsTable;
